import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Fade,
  Alert,
  Snackbar,
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import { useTheme } from '../app/ThemeProvider';
import { EmptyState, Footer } from '../shared/ui';
import {
  useInvoicePortal,
  usePaymentLink,
  usePaymentMethods,
} from '../shared/hooks';
import {
  InvoiceCard,
  SimpleNavbar,
  DebtorHeader,
  PaymentOptionsDialog,
} from '../features/invoice-portal';
import { InvoicePortalItem } from '../entities/InvoicePortal';
import { PaymentMethodType } from '../entities/PaymentMethods';

const InvoicePortalPage: React.FC = () => {
  const { uuid } = useParams<{ uuid: string }>();
  const { mode, toggleTheme } = useTheme();
  const { data, isLoading, error, refetch } = useInvoicePortal(uuid);
  const { data: paymentMethods } = usePaymentMethods(uuid);
  const {
    createPaymentLink,
    isLoading: isCreatingPayment,
    error: paymentError,
    clearError,
  } = usePaymentLink();
  const [processingInvoiceId, setProcessingInvoiceId] = useState<number | null>(
    null
  );
  const [paymentOptionsDialogOpen, setPaymentOptionsDialogOpen] =
    useState(false);
  const [selectedInvoice, setSelectedInvoice] =
    useState<InvoicePortalItem | null>(null);
  const [highlightPaymentMethod, setHighlightPaymentMethod] = useState<
    PaymentMethodType | undefined
  >(undefined);

  const handlePayInvoice = async (
    invoiceId: number,
    paymentMethod?: PaymentMethodType
  ) => {
    try {
      setProcessingInvoiceId(invoiceId);

      if (paymentMethod === 'mercadopago' || !paymentMethod) {
        // Para MercadoPago o método no especificado, usar el flujo existente
        await createPaymentLink(invoiceId);
      } else if (paymentMethod === 'bank_transfer') {
        // Mostrar información de transferencia bancaria
        setSelectedInvoice(
          data?.invoices.find((inv) => inv.id === invoiceId) || null
        );
        setHighlightPaymentMethod('bank_transfer');
        setPaymentOptionsDialogOpen(true);
      } else if (paymentMethod === 'cash_payment') {
        // Mostrar información de pago presencial
        setSelectedInvoice(
          data?.invoices.find((inv) => inv.id === invoiceId) || null
        );
        setHighlightPaymentMethod('cash_payment');
        setPaymentOptionsDialogOpen(true);
      }
    } catch (error) {
      console.error('Error al procesar el pago:', error);
    } finally {
      setProcessingInvoiceId(null);
    }
  };

  const handlePaymentPromise = (invoiceId: number) => {
    // TODO: Implementar lógica de promesa de pago
    console.log('Realizar promesa de pago para factura:', invoiceId);
  };

  const handleShowPaymentOptions = (invoice: InvoicePortalItem) => {
    setSelectedInvoice(invoice);
    setHighlightPaymentMethod(undefined); // No resaltar ningún método específico
    setPaymentOptionsDialogOpen(true);
  };

  const handleClosePaymentOptions = () => {
    setPaymentOptionsDialogOpen(false);
    setSelectedInvoice(null);
    setHighlightPaymentMethod(undefined);
  };

  const handlePaymentMethodSelect = (methodType: PaymentMethodType) => {
    console.log('Método de pago seleccionado:', methodType);
    // TODO: Implementar lógica específica para cada método de pago
    // Por ejemplo, mostrar instrucciones adicionales o registrar la selección
  };

  // Mostrar loading mientras valida o carga datos
  if (isLoading) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
            textAlign: 'center',
            px: 3,
          }}
        >
          <CircularProgress
            size={40}
            thickness={4}
            sx={{ mb: 3, color: 'primary.main' }}
          />
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: 600,
              fontSize: '1.125rem',
              letterSpacing: '-0.01em',
            }}
          >
            Cargando detalles de pago
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Por favor espera mientras cargamos tu información...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Mostrar error si UUID no es válido o hay error en los datos
  if (error) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            mt: 6,
            px: 3,
            minHeight: '50vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              bgcolor: 'background.paper',
              borderRadius: 3,
              p: 4,
              textAlign: 'center',
              border: '1px solid',
              borderColor: 'rgba(0, 0, 0, 0.05)',
              width: '100%',
              maxWidth: 400,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: 'error.main',
                mb: 2,
                fontSize: '1.125rem',
                letterSpacing: '-0.01em',
              }}
            >
              No se pudieron cargar los detalles
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {error}
            </Typography>
            <Box sx={{ mt: 3 }}>
              <IconButton
                onClick={refetch}
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  width: 48,
                  height: 48,
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
              >
                <Refresh />
              </IconButton>
              <Typography
                variant="body2"
                sx={{
                  mt: 2,
                  fontWeight: 500,
                  color: 'text.secondary',
                }}
              >
                Intentar de nuevo
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
    );
  }

  if (!data) {
    return <EmptyState type="not-found" />;
  }

  return (
    <Fade in timeout={800}>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
        {/* Navbar - ocupa todo el ancho superior */}
        <SimpleNavbar companyInfo={data.company_info} />

        {/* Nombre del deudor y deuda total */}
        <DebtorHeader
          debtorInfo={data.debtor_info}
          totalDebt={data.total_debt}
          currency={data.currency}
        />

        {/* Listado de facturas */}
        <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ py: { xs: 3, sm: 4 } }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: { xs: 3, sm: 4 },
                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                letterSpacing: '-0.01em',
              }}
            >
              Facturas adeudadas
            </Typography>

            {!data?.invoices || data.invoices.length === 0 ? (
              <Box
                sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 3,
                  p: 4,
                  textAlign: 'center',
                  border: '1px solid',
                  borderColor: 'rgba(0, 0, 0, 0.05)',
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: 'success.main', mb: 1 }}
                >
                  🎉 ¡Todo al día!
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  No tienes facturas pendientes en este momento.
                </Typography>
              </Box>
            ) : (
              <Box>
                {data?.invoices?.map((invoice) => (
                  <Fade key={invoice.id} in timeout={600}>
                    <Box>
                      <InvoiceCard
                        invoice={invoice}
                        onPay={handlePayInvoice}
                        onPaymentPromise={handlePaymentPromise}
                        onShowPaymentOptions={handleShowPaymentOptions}
                        availablePaymentMethods={
                          paymentMethods?.available_methods
                        }
                        isProcessingPayment={processingInvoiceId === invoice.id}
                      />
                    </Box>
                  </Fade>
                ))}
              </Box>
            )}
          </Box>
        </Container>

        {/* Footer */}
        <Footer />

        {/* Snackbar para errores de pago */}
        <Snackbar
          open={!!paymentError}
          autoHideDuration={6000}
          onClose={clearError}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="error" onClose={clearError} sx={{ width: '100%' }}>
            {paymentError}
          </Alert>
        </Snackbar>

        {/* Diálogo de opciones de pago */}
        {selectedInvoice && (
          <PaymentOptionsDialog
            open={paymentOptionsDialogOpen}
            onClose={handleClosePaymentOptions}
            paymentMethods={paymentMethods || undefined}
            invoiceNumber={`${selectedInvoice.voucher_type_display} ${selectedInvoice.number}`}
            invoiceAmount={selectedInvoice.total}
            currency={selectedInvoice.currency}
            onPayWithMercadoPago={() => handlePayInvoice(selectedInvoice.id)}
            onPaymentMethodSelect={handlePaymentMethodSelect}
            highlightMethod={highlightPaymentMethod}
            showOnlyHighlighted={!!highlightPaymentMethod}
          />
        )}
      </Box>
    </Fade>
  );
};

export default InvoicePortalPage;
